import { PaginatedQueryResult } from '@query/usePaginatedQuery'
import { FC, ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from '@app/context/SnackbarContext'

import { SubIBAccountDetailsModal } from '@pages/IntroducingBroker/MultiTier/SubIBAccounts/modals/SubIBAccountDetails.modal'

import { useModal } from '@context/ModalsContext'

import { IconButton } from '@ui/components/Button'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { EditIcon } from '@ui/icons/Edit.icon'
import { FileIcon } from '@ui/icons/File.icon'
import { KebabIcon } from '@ui/icons/Kebab.icon'
import {
  SensitiveTableCell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'

import { useWindowResize } from '@utils/domUtils'

import { SubIBAccount, SubIbAccountStatus } from '@model/MultiTierDto'

import { ActionsListModal } from '@shared/modals/ActionsList.modal'

interface Props {
  sticky?: 'left'
  onEdit: (account: SubIBAccount) => void
  searchValue: string | null
  tableQuery: PaginatedQueryResult<SubIBAccount>
}

export const SubIBAccountsTable: FC<Props> = (props) => {
  const { t } = useTranslation()
  const { tableQuery, onEdit, sticky, searchValue } = props
  const { data: subIBAccounts, isLoading } = tableQuery
  const isMobile = useWindowResize()
  const { addSnackbar } = useSnackbar()

  const subIBAccountsActionsModal = useModal(ActionsListModal)
  const subIBAccountsDetailsModal = useModal(SubIBAccountDetailsModal)

  useEffect(() => {
    if (isMobile) {
      return
    } else {
      subIBAccountsActionsModal.close()
      subIBAccountsDetailsModal.close()
    }
  }, [isMobile, subIBAccountsActionsModal, subIBAccountsDetailsModal])

  const handleEdit = (account: SubIBAccount) => {
    if (account.status.id === SubIbAccountStatus.Inactive) {
      isMobile && addSnackbar.info({ message: t('IB.MultiTier.IB is disabled') })
      return
    }

    onEdit(account)
    subIBAccountsActionsModal.close()
    subIBAccountsDetailsModal.close()
  }

  const renderDesktopOnly = (node: ReactNode) => (isMobile ? null : node)

  return (
    <Table
      isLoading={isLoading}
      noResults={
        !subIBAccounts.length && (
          <TableNoResults
            title={
              searchValue ? t('No results found') : t('IB.MultiTier.No Sub-IB Accounts to display')
            }
          />
        )
      }
      sticky={sticky}
      isLayoutFixed={isMobile}
    >
      <TableHeader>
        <TableRow>
          <TableHead width={isMobile ? 145 : undefined}>
            {t('IB.MultiTier.Sub-IB Accounts')}
          </TableHead>
          <TableHead width={isMobile ? 126 : undefined}>{t('IB.Name')}</TableHead>
          {renderDesktopOnly(
            <TableHead align='right'>{t('IB.MultiTier.IB Commission')}</TableHead>
          )}
          {renderDesktopOnly(<TableHead align='right'>{t('IB.MultiTier.Plan')}</TableHead>)}
          {renderDesktopOnly(<TableHead align='right'>{t('IB.Volume')}</TableHead>)}
          <TableHead width={isMobile ? 57 : undefined} />
        </TableRow>
      </TableHeader>
      <TableBody>
        {subIBAccounts.map((subIBAccount, index) => (
          <TableRow key={subIBAccount.account.id}>
            <SensitiveTableCell>{subIBAccount.account.name}</SensitiveTableCell>
            <SensitiveTableCell>{subIBAccount.clientName}</SensitiveTableCell>
            {renderDesktopOnly(
              <SensitiveTableCell align='right'>{subIBAccount.ibCommission}</SensitiveTableCell>
            )}
            {renderDesktopOnly(
              <SensitiveTableCell align='right'>
                {subIBAccount.commissionPlanGroup.name}
              </SensitiveTableCell>
            )}
            {renderDesktopOnly(
              <SensitiveTableCell align='right'>{subIBAccount.volume}</SensitiveTableCell>
            )}
            <TableCell align='right'>
              {isMobile ? (
                <IconButton.Ghost
                  icon={KebabIcon}
                  onClick={() =>
                    subIBAccountsActionsModal.open({
                      actions: [
                        {
                          label: t('Details'),
                          icon: FileIcon,
                          onClick: () =>
                            subIBAccountsDetailsModal.open({
                              accounts: subIBAccounts,
                              initialIndex: index,
                              onEdit: handleEdit,
                            }),
                        },
                        {
                          label: t('Edit'),
                          icon: EditIcon,
                          onClick: () => handleEdit(subIBAccount),
                        },
                      ],
                    })
                  }
                />
              ) : (
                <Tooltip
                  direction='top'
                  content={t(
                    subIBAccount.status.id === SubIbAccountStatus.Inactive
                      ? 'IB.MultiTier.IB is disabled'
                      : 'Edit'
                  )}
                >
                  <IconButton.Ghost icon={EditIcon} onClick={() => handleEdit(subIBAccount)} />
                </Tooltip>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
